import { useMutation, useQueryClient, type QueryClient, type QueryKey } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { type IConversation, ReadStatus } from 'src/api/landlord-messages-api';
import type { IStandardResponse } from 'src/common/api-utils';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';

const PATH = 'api/landlord/messages/read-status';

type Response = IStandardResponse;

// The endpoint also takes message_uuids, but nothing has called that shape since
// #605 and a set of message uuids cannot yield a per-lead unread count anyway.
type Request = {
    status: ReadStatus;
    leadUuids: string[];
};

export const useUpdateConversationsReadStatus = () => {
    const queryClient = useQueryClient();

    return useMutation<Response, IStandardErrorResponse, Request>(
        (payload) => {
            const json = {
                read_status: payload.status,
                lead_uuids: payload.leadUuids,
            };

            return apiClient(PATH, {
                method: 'patch',
                json,
                timeout: 30000
            }).json<IStandardResponse>();
        }, {
            onSuccess: (_response, { status, leadUuids }) => {
                const affectedLeadUuids = new Set(leadUuids);
                patchExistingCache<IConversation[]>(queryClient, [queryKeys.conversations], (conversations) => (
                    conversations.map((conversation) => (
                        affectedLeadUuids.has(conversation.lead_uuid) ?
                            patchReadStatus(conversation, status) :
                            conversation
                    ))
                ));

                affectedLeadUuids.forEach((leadUuid) => patchExistingCache<IConversation>(
                    queryClient,
                    [queryKeys.conversations, 'lead', leadUuid],
                    (conversation) => patchReadStatus(conversation, status),
                ));
            },
        }
    );
};

function patchReadStatus(conversation: IConversation, status: ReadStatus): IConversation {
    return {
        ...conversation,
        num_unread_messages: status === ReadStatus.READ ?
            0 :
            Math.max(conversation.num_unread_messages, 1),
    };
}

// React Query v3 can create a fresh cache entry even for an undefined update.
function patchExistingCache<TData>(
    queryClient: QueryClient,
    key: QueryKey,
    patch: (cached: TData) => TData,
) {
    const cached = queryClient.getQueryData<TData>(key);
    if (cached) {
        queryClient.setQueryData<TData>(key, patch(cached));
    }
}
